import React, { useRef } from 'react';
import Button from '../ui/Button';
import VariableProximity from '../ui/VariableProximity';
import Tooltip from '../ui/Tooltip';
import { HelpCircle } from 'lucide-react';

const ScrollIndicator: React.FC = () => {
  const handleScroll = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const introSection = document.getElementById('intro');
    if (introSection) {
      introSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <button
      onClick={handleScroll}
      className="absolute bottom-10 left-1/2 -translate-x-1/2 group focus-ring rounded-full"
      aria-label="Scroll to introduction section"
    >
      <div className="glow-card card-backdrop w-6 h-10 border-2 border-foreground rounded-full flex justify-center p-1 transition-transform duration-200 hover:scale-[1.03]">
        <div className="w-1 h-2 bg-foreground rounded-full animate-bounce" aria-hidden="true" />
      </div>
      <span className="sr-only">Scroll down</span>
    </button>
  );
};

const HeroSection: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  return (
    <section 
      id="hero"
      className="relative min-h-screen flex items-center pt-24 pb-20"
      aria-labelledby="hero-heading"
    >
      <div className="section-grain" aria-hidden="true" />
      <div className="container relative z-10 mx-auto px-3 sm:px-4 lg:px-6">
        <div className="max-w-4xl" ref={containerRef}>
          <p 
            className="font-mono text-sm uppercase tracking-widest text-foreground/80 animate-on-scroll" 
            data-delay="0.2s"
          >
            Branding | UX/UI | Websites | CCTV
          </p>
          
          {/* Mobile and Tablet: Static text with weight 900 */}
          <h1 
            id="hero-heading-mobile"
            className="block lg:hidden font-display text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter text-foreground mt-4 animate-on-scroll leading-tight"
            style={{ fontWeight: 900 }}
          >
            <span className="block">We Architect</span>
            <span className="block">Digital</span>
            <span className="block">Experiences</span>
            <span className="block">that <span className="text-primary brand-readable">Connect</span></span>
            <span className="block">& Convert.</span>
          </h1>
          
          {/* Desktop: Animated text with proximity effect */}
          <h1 
            id="hero-heading"
            className="hidden lg:block font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter text-foreground mt-4 animate-on-scroll leading-tight"
          >
            <span className="block">
              <VariableProximity
                label="We Architect"
                fromFontVariationSettings="'wght' 750, 'ital' 0"
                toFontVariationSettings="'wght' 900, 'ital' 1"
                containerRef={containerRef}
                radius={150}
                falloff="exponential"
                fontFamily="inherit"
              />
            </span>
            <span className="block">
              <VariableProximity
                label="Digital"
                fromFontVariationSettings="'wght' 750, 'ital' 0"
                toFontVariationSettings="'wght' 900, 'ital' 1"
                containerRef={containerRef}
                radius={150}
                falloff="exponential"
                fontFamily="inherit"
              />
            </span>
            <span className="block">
              <VariableProximity
                label="Experiences"
                fromFontVariationSettings="'wght' 750, 'ital' 0"
                toFontVariationSettings="'wght' 900, 'ital' 1"
                containerRef={containerRef}
                radius={150}
                falloff="exponential"
                fontFamily="inherit"
              />
            </span>
            <span className="block">
              <VariableProximity
                label="that "
                fromFontVariationSettings="'wght' 750, 'ital' 0"
                toFontVariationSettings="'wght' 900, 'ital' 1"
                containerRef={containerRef}
                radius={150}
                falloff="exponential"
                fontFamily="inherit"
              />
              <span className="text-primary brand-readable">
                <VariableProximity
                  label="Connect"
                  fromFontVariationSettings="'wght' 750, 'ital' 0"
                  toFontVariationSettings="'wght' 900, 'ital' 1"
                  containerRef={containerRef}
                  radius={150}
                  falloff="exponential"
                  fontFamily="inherit"
                />
              </span>
            </span>
            <span className="block">
              <VariableProximity
                label="& Convert."
                fromFontVariationSettings="'wght' 750, 'ital' 0"
                toFontVariationSettings="'wght' 900, 'ital' 1"
                containerRef={containerRef}
                radius={150}
                falloff="exponential"
                fontFamily="inherit"
              />
            </span>
          </h1>
          
          <div 
            className="mt-6 md:mt-8 text-lg md:text-xl text-foreground/70 max-w-2xl animate-on-scroll relative space-y-1" 
            data-delay="0.4s"
          >
            <p>
              <span className="block md:inline">From brand to build —</span>
              <span className="block md:inline">
                {' '}logos, websites, and{' '}
                <span className="inline-flex items-center gap-2">
                  on-prem CCTV
                  <Tooltip content="Plug-and-play server that streams your footage securely — no port forwarding, no hassle.||Secure, risk-free, fully embeddable on your website.">
                    <HelpCircle 
                      size={16} 
                      className="text-foreground/70 hover:text-primary transition-colors cursor-help flex-shrink-0" 
                    />
                  </Tooltip>
                </span>
                .
              </span>
            </p>
            <p>Simple, smart, future-ready.</p>
          </div>
          
          <div className="mt-8 md:mt-10 animate-on-scroll" data-delay="0.6s">
            <Button 
              onClick={() => {
                const el = document.getElementById('services');
                if (el) {
                  const offset = 80;
                  const top = el.offsetTop - offset;
                  window.scrollTo({ top, behavior: 'smooth' });
                }
              }}
              variant="primary"
              size="lg"
              rightIcon={
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              }
            >
              Explore Our Services
            </Button>
          </div>
        </div>
      </div>
      
      <ScrollIndicator />
    </section>
  );
};

export default HeroSection;
